/**
 * Manifest-backed permission abstraction.
 *
 * Intentionally permix-shaped (`createPermissions(...).can(key, right)`) so it
 * can be swapped for an external permissions library later without touching
 * call sites. The authorization model lives in MANIFEST — this only evaluates
 * a user's authority set against it.
 */
import { MANIFEST, ManifestEntry } from './manifest'

export type Right = 'read' | 'write'

/**
 * CRUD-flavored actions used at call sites. The manifest only distinguishes two
 * authority tiers (read vs write), so every mutating verb maps to `write`:
 *   read                           -> read authorities (write implies read)
 *   create | edit | delete | write -> write authorities
 * This keeps the ergonomic vocabulary (canCreate/canEdit/canDelete) while the
 * underlying authority model stays as granular as it actually is.
 */
export type Action = 'read' | 'create' | 'edit' | 'delete' | 'write'

const actionToRight = (action: Action): Right =>
  action === 'read' ? 'read' : 'write'

export type ManifestKey = keyof typeof MANIFEST

/** The authorities that grant a given right on an entry. */
function requiredAuthorities(
  key: ManifestKey,
  right: Right
): ReadonlyArray<string> {
  const entry: ManifestEntry = MANIFEST[key]
  if (right === 'write') return entry.write ?? []
  return entry.read
}

export interface Permissions {
  /**
   * Whether the user holds any authority granting `action` on `key`.
   * Write-tier actions (create/edit/delete/write) require write; read is
   * satisfied by read or write authorities.
   */
  can(key: ManifestKey, action?: Action): boolean
  /** Authorities that would grant `action` on `key` (for "Requires: X"). */
  missing(key: ManifestKey, action?: Action): string[]
}

export function createPermissions(
  userAuthorities: ReadonlyArray<string>
): Permissions {
  const held = new Set(userAuthorities)

  const holdsAny = (authorities: ReadonlyArray<string>): boolean =>
    authorities.some(a => a !== '' && held.has(a))

  return {
    can(key, action = 'read') {
      if (actionToRight(action) === 'read') {
        // write implies read
        return (
          holdsAny(requiredAuthorities(key, 'read')) ||
          holdsAny(requiredAuthorities(key, 'write'))
        )
      }
      return holdsAny(requiredAuthorities(key, 'write'))
    },
    missing(key, action = 'read') {
      return requiredAuthorities(key, actionToRight(action)).filter(
        a => a !== ''
      )
    },
  }
}
